import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { PageRequestDTO, PageResponseDTO } from '../../../common/paging-dto';
import { FieldConfigDTO, MaintainConfigDTO, MenuDTO, MenuType } from '../../../system/system-dtos';
import { KibanaQueryDTO } from '../../../common/kibana-request-dto';
import { QuickFilterDTO } from '../../data-type-widgets/data-type/lottomart/model/lottomart-model';
import { MenuUtils } from '../../../common/utils/menu-utils';
import { CassandraQueryDTO } from '../../../common/cassandra-request-dto';

@Injectable()
export class MaintainService {
  public static readonly KIBANA_QUERY_URL = '/kibana/query';

  public static readonly CASSANDRA_QUERY_URL = '/cassandra/query';

  public rowActionsChangeSubscriber = new Subject();

  constructor(private http: HttpClient) {}

  selectAllData(path: string, pageRequest: PageRequestDTO, extraParams: HttpParams): Observable<PageResponseDTO<any>> {
    let params = (extraParams || new HttpParams())
      .append('offset', Number(pageRequest.offset).toFixed())
      .append('per-page', Number(pageRequest.perPage).toFixed());

    if (pageRequest.order && !pageRequest.order.includes('null')) {
      params = params.append('order', pageRequest.order);
    }

    let paramsString = params.toString();
    if (path?.includes('?')) {
      paramsString = `&${paramsString}`;
    } else {
      paramsString = `?${paramsString}`;
    }

    return this.http.get<PageResponseDTO<any>>(path + paramsString);
  }

  selectOne(url: string, extraParams: HttpParams = null, body = {}, method = 'GET'): Observable<any> {
    const params = extraParams || new HttpParams();
    return this.http.request(method, url, { params, body });
  }

  updateEntity(body: any, url: string, extraParams: HttpParams = null): Observable<any> {
    const params = extraParams || new HttpParams();
    const newUrl = this.replaceUrlParams(body, url);
    // return this.http.put<any>(url, body, { params });
    return this.http.put<any>(newUrl, body, { params });
  }

  /*
    replace /contentdocs/{code}/{locale.code}/{version}
    with /contentdocs/CLOSE_ACCOUNT_PAGE/EN/1
    */
  replaceUrlParams(body: any, url: string) {
    const newUrl = url.replace(/({(.*?)})/g, (field) => {
      const name = field.replace(/{/, '').replace(/}/, '');
      if (name !== undefined && name.indexOf('.') > 0) {
        const nameSplit = name.split('.');
        let dataObject = body;

        for (let x = 0; x < nameSplit.length; x++) {
          if (dataObject[nameSplit[x]] === undefined) {
            // not found
            return name;
          }
          dataObject = dataObject[nameSplit[x]];
        }
        return dataObject;
      }
      if (name !== undefined) {
        return body[name];
      }
      return field;
    });
    return newUrl;
  }

  createAppIdForEntity(menuConf: MenuDTO, data: any): string {
    const firstMaintainConfig = MenuUtils.getFirstMaintainConfig(menuConf.entityGroup);

    let selectOneUrl = menuConf.entityGroup.selectUrl;
    if (firstMaintainConfig) {
      selectOneUrl = firstMaintainConfig.tabs[0].endpoints.selectOneUrl || menuConf.entityGroup.selectUrl;
    }
    return this.extractId(selectOneUrl, data);
  }

  extractId(url: string, data: any) {
    const matches = this.getFields(url);
    return matches.length > 1
      ? matches
          .map((m) => this.getDataFromFieldName(m, data))
          // .reduce((acc, v) => `${acc}&${v}`)
          .reduce((acc, v) => `${acc}/${v}`)
      : this.getDataFromFieldName(matches[0], data);
  }

  public getFields(url: string): string[] {
    const regex = /({.+?})/g;
    const matches: string[] = [];
    let m: RegExpExecArray;
    while ((m = regex.exec(url))) {
      matches.push(m[0]);
    }
    return matches;
  }

  resolveAppIdForEntity(id: string, menuConf: MenuDTO): any {
    const firstMaintainConfig = MenuUtils.getFirstMaintainConfig(menuConf.entityGroup);
    const url = firstMaintainConfig
      ? firstMaintainConfig.tabs[0].endpoints.selectOneUrl
      : menuConf.entityGroup.selectUrl;
    return this.resolveIdForUrl(id, url);
  }

  resolveIdForUrl(id: string, url: string) {
    const ids = id.split('&');
    const fields = this.getFields(url).map((f) => f.substr(1, f.length - 2));

    const ret = {};
    for (let i = 0; i < ids.length; i++) {
      ret[fields[i]] = ids[i];
    }

    return ret;
  }

  private getDataFromFieldName(codeName: string, data: any): any {
    const fieldName = codeName.substr(1, codeName.length - 2);
    const splitted = fieldName.split('.');

    return splitted.length > 1 ? data[splitted[0]][splitted[1]] : data[fieldName];
  }

  createEntity(body: any, insertUrl: string) {
    return this.http.post<any>(insertUrl, body);
  }

  performKibanaQuery(kibanaQuery: KibanaQueryDTO): Observable<PageResponseDTO<any>> {
    return this.http.post<PageResponseDTO<any>>(MaintainService.KIBANA_QUERY_URL, kibanaQuery);
  }

  performKibanaQueryWithUrl(kibanaQuery: KibanaQueryDTO, url: string): Observable<PageResponseDTO<any>> {
    return this.http.post<PageResponseDTO<any>>(url, kibanaQuery);
  }

  getElasticFilterForGroup(group: string): Observable<QuickFilterDTO[]> {
    return this.http.get<QuickFilterDTO[]>(`/quick-filter-groups/${group}`);
  }

  performCassandraQuery(cassandraQuery: CassandraQueryDTO): Observable<PageResponseDTO<any>> {
    return this.http.post<PageResponseDTO<any>>(MaintainService.CASSANDRA_QUERY_URL, cassandraQuery);
  }

  performCassandraQueryWithUrl(cassandraQuery: CassandraQueryDTO, url: string): Observable<PageResponseDTO<any>> {
    return this.http.post<PageResponseDTO<any>>(url, cassandraQuery);
  }
}
